import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Flow from './Flow/Flow';
import DialogActions from '@mui/material/DialogActions';
import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useState } from 'react';
import { Scheme, SchemePreviewData } from '../types';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import LinkIcon from '@mui/icons-material/Link';
import { Skeleton, Snackbar } from '@mui/material';
import { Alert } from '@mui/lab';
import AccessChip from '@shared/components/others/AccessChip/AccessChip';
import OverflowChipList from '@shared/components/others/OverflowChipList/OverflowChipList';
import { Tag } from '@features/tags/types';
import { SchemesListOptionalCallbacks } from './SchemesList';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { getSchemeByIdRequest, getSchemeByIdReset } from '../store';
import { selectGetSchemeStatus, selectScheme } from '../store/selectors';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useSchemesChunk } from '../hooks/useSchemesChunk';
import { UserProfile } from '@features/users';
import { ModuleID } from '@pages/modules/types';
import { selectModulesLookup } from '@pages/modules/store/selectors';

export type SchemeRequiredFieldsWithOptional = Partial<Scheme> & Required<SchemePreviewData>;

export const SchemePreviewDialogChunk = (props: SchemePreviewDialogProps) => {
    return (
        <ComponentWithChunks
            Component={SchemePreviewDialogComponent}
            featuresArray={['schemes']}
            hooksArray={[useSchemesChunk]}
            {...props}
        />
    );
};

interface SchemePreviewDialogProps {
    scheme: SchemeRequiredFieldsWithOptional;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    handleCopyLink?: SchemesListOptionalCallbacks['handleCopyLink'];
    fetchDataOnOpen?: boolean;
}

const SchemePreviewDialogComponent: FC<SchemePreviewDialogProps> = ({
    scheme,
    open,
    setOpen,
    handleCopyLink,
    fetchDataOnOpen,
}) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [schemeState, setSchemeState] = useState<SchemeRequiredFieldsWithOptional>(structuredClone(scheme));
    const modulesLookup = useSelector(selectModulesLookup);
    const fetchedScheme = useSelector(selectScheme);
    const fetchSchemeStatus = useSelector(selectGetSchemeStatus);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!fetchDataOnOpen) {
            return;
        }

        if (open) {
            // @ts-ignore
            dispatch(getSchemeByIdRequest(scheme.pk));
        } else {
            dispatch(getSchemeByIdReset());
        }
    }, [open, fetchDataOnOpen, scheme.pk, dispatch]);

    useEffect(() => {
        if (fetchDataOnOpen && fetchedScheme) {
            setSchemeState((schemeState) => ({ ...schemeState, ...structuredClone(fetchedScheme) }));
        } else if (fetchDataOnOpen && !fetchedScheme) {
            setSchemeState(structuredClone(scheme));
        }
    }, [fetchDataOnOpen, fetchedScheme, scheme]);

    const handleCloseDialog = (e: SyntheticEvent) => {
        e.stopPropagation();
        setOpen(false);
    };

    return (
        <Dialog
            maxWidth={false}
            onClose={handleCloseDialog}
            onClick={(e) => e.stopPropagation()}
            open={open}
            PaperProps={{
                sx: { minHeight: 750, width: 1700, backgroundColor: 'var(--section-bg-color)' },
            }}
        >
            <DialogTitle variant="h4">
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {`${t('scheme')} - ${schemeState.name}`}
                    {schemeState.access && <AccessChip access={schemeState.access} />}
                    {fetchDataOnOpen && fetchSchemeStatus === REQUEST_STATUS.LOADING && (
                        <Skeleton width="100px" height="24px" />
                    )}
                    {schemeState.created_by_user && (
                        <UserProfile
                            createdUserId={schemeState.created_by_user}
                            showAvatar
                            userContainerProps={{
                                sx: { border: 'none', marginLeft: 'auto' },
                            }}
                        />
                    )}
                    {fetchDataOnOpen && fetchSchemeStatus === REQUEST_STATUS.LOADING && (
                        <Skeleton sx={{ marginLeft: 'auto' }} width="100px" height="24px" />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 0 }}>
                <Box sx={{ height: '24px' }}>
                    {schemeState.modules && Boolean(schemeState.modules.length) ? (
                        Boolean(Object.keys(modulesLookup).length) && (
                            <OverflowChipList
                                labelAtStart={`${t('modules')}:`}
                                array={schemeState.modules}
                                getLabel={(moduleId: ModuleID) => modulesLookup[moduleId].name}
                                getId={(moduleId: ModuleID) => moduleId}
                                chipProps={{
                                    sx: {
                                        background: 'var(--secondary-main)',
                                        color: 'var(--secondary-contrast)',
                                    },
                                }}
                            />
                        )
                    ) : (
                        <Box sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                            {`${t('modules')}:`}
                            {fetchDataOnOpen && fetchSchemeStatus === REQUEST_STATUS.LOADING && (
                                <Skeleton width={300} height="100%" />
                            )}
                        </Box>
                    )}
                </Box>
                <Box sx={{ height: '24px' }}>
                    {schemeState.tags && Boolean(schemeState.tags.length) ? (
                        <OverflowChipList
                            labelAtStart={`${t('tags')}:`}
                            array={schemeState.tags}
                            getLabel={(tag: Tag) => tag.text}
                            getId={(tag: Tag) => tag.pk}
                            lineCount={2}
                        />
                    ) : (
                        <Box sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                            {`${t('tags')}:`}
                            {fetchDataOnOpen && fetchSchemeStatus === REQUEST_STATUS.LOADING && (
                                <Skeleton width={300} height="100%" />
                            )}
                        </Box>
                    )}
                </Box>
                {fetchDataOnOpen && fetchSchemeStatus === REQUEST_STATUS.LOADING && (
                    <Skeleton sx={{ transform: 'none !important' }} width="100%" height="620px" />
                )}
                {schemeState.json_config && (
                    <Box sx={{ background: 'var(--section-bg-color)', borderRadius: 1, height: '620px' }}>
                        <Flow
                            readonly
                            nodes={schemeState.json_config.nodes || []}
                            edges={schemeState.json_config.edges || []}
                            containerProps={undefined}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: '8px 16px', justifyContent: 'flex-start' }}>
                {handleCopyLink && (
                    <>
                        <UtmsButton
                            onClick={(e: SyntheticEvent) => {
                                e.stopPropagation();
                                handleCopyLink(schemeState.pk);
                                setSnackbarOpen(true);
                            }}
                            startIcon={<LinkIcon />}
                        >
                            {t('copy_link')}
                        </UtmsButton>
                        <Snackbar
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                            open={snackbarOpen}
                            autoHideDuration={1000}
                            onClose={() => setSnackbarOpen(false)}
                        >
                            <Alert severity="success" sx={{ width: '100%' }}>
                                {t('copied_to_clipboard')}
                            </Alert>
                        </Snackbar>
                    </>
                )}
                <UtmsButton
                    sx={{ marginLeft: 'auto !important' }}
                    color="secondary"
                    onClick={handleCloseDialog}
                    size="small"
                >
                    {t('close')}
                </UtmsButton>
            </DialogActions>
        </Dialog>
    );
};
